import { useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import {
  ArtistEntitlement,
  EntitlementStatus,
  artistEntitlementGrant,
  artistEntitlementList,
  artistEntitlementRevoke,
} from "@/services/firebase/artistHub";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { AdminStatusBadge } from "@/components/admin-ui";
import type { AdminStatusTone } from "@/components/admin-ui/admin-status-badge";

const statusLabels: Record<ArtistEntitlement["status"], string> = {
  active: "有效",
  grace: "寬限中",
  expired: "已到期",
  revoked: "已撤銷",
};
const sourceLabels: Record<string, string> = {
  coin: "金幣開通",
  staff_grant: "人工發放",
  iap: "App 內購",
  promo: "活動",
};
const statusTone: Record<ArtistEntitlement["status"], AdminStatusTone> = {
  active: "success",
  grace: "warning",
  expired: "neutral",
  revoked: "neutral",
};
const statusFilters: { value: EntitlementStatus | ""; label: string }[] = [
  { value: "", label: "全部" },
  { value: "active", label: "有效" },
  { value: "grace", label: "寬限中" },
  { value: "expired", label: "已到期" },
  { value: "revoked", label: "已撤銷" },
];
const fmt = (ms: number | null) => (ms ? new Date(ms).toLocaleString("zh-TW", { hour12: false }) : "—");
// <input type="date"> value (local calendar day) → end of that day in local time, as epoch ms.
const endOfDay = (value: string): number | null => {
  if (!/^\d{4}-\d{2}-\d{2}$/.test(value)) return null;
  const [y, m, d] = value.split("-").map(Number);
  return new Date(y, m - 1, d, 23, 59, 59, 999).getTime();
};
const defaultUntil = () => {
  const d = new Date();
  d.setDate(d.getDate() + 30);
  return d.toISOString().slice(0, 10);
};

/**
 * B-17: 鐵粉名單. Staff hand out / revoke 鐵粉 entitlements (source staff_grant, validUntil required) for
 * testing and gifts; fans content stays locked for everyone else.
 * B-23: shows coin fan-pass fields (source coin, status grace + graceUntil, autoRenew, streakMonths) and filters by status.
 * Entitlements are server-only writes (artistEntitlements/{uid}/artists/{artistId}); this panel only calls artistHub.
 */
export default function ArtistFansPanel({ artistId }: { artistId: string }) {
  const client = useQueryClient();
  const [statusFilter, setStatusFilter] = useState<EntitlementStatus | "">("");
  const listKey = ["artist-entitlements", artistId];
  const list = useQuery({
    queryKey: [...listKey, statusFilter],
    queryFn: () => artistEntitlementList(artistId, null, 50, statusFilter || null),
    retry: false,
  });
  const [email, setEmail] = useState("");
  const [until, setUntil] = useState(defaultUntil);
  const [note, setNote] = useState("");
  const [message, setMessage] = useState("");
  const grant = useMutation({
    mutationFn: () => {
      const validUntil = endOfDay(until);
      if (!validUntil) throw new Error("請選擇到期日");
      if (validUntil <= Date.now()) throw new Error("到期日必須晚於現在");
      return artistEntitlementGrant(artistId, { email: email.trim() }, validUntil, note.trim());
    },
    onSuccess: (r) => {
      setMessage(`已發放給 ${email.trim()}（到 ${fmt(r.validUntil)}）`);
      setEmail("");
      setNote("");
      void client.invalidateQueries({ queryKey: listKey });
      void client.invalidateQueries({ queryKey: ["artist-audit", artistId] });
    },
  });
  const revoke = useMutation({
    mutationFn: (uid: string) => artistEntitlementRevoke(artistId, uid),
    onSuccess: (r) => {
      setMessage(`已撤銷 ${r.uid} 的鐵粉權益`);
      void client.invalidateQueries({ queryKey: listKey });
      void client.invalidateQueries({ queryKey: ["artist-audit", artistId] });
    },
  });
  const busy = grant.isPending || revoke.isPending;
  const error = grant.error ?? revoke.error ?? (list.isError ? list.error : null);
  return (
    <Card className="mb-6">
      <CardHeader>
        <CardTitle>鐵粉名單（鐵粉限定內容的觀看權益）</CardTitle>
      </CardHeader>
      <CardContent className="space-y-4">
        <p className="text-sm text-muted-foreground">
          標為「鐵粉限定」的內容只有名單內且仍在效期內的帳號看得到完整內容，其餘人只看到鎖定卡（標題、前 40 字、計數）。
          粉絲在 App 以購買金幣開通鐵粉月卡（來源「金幣開通」，預設自動續期）；工作人員也可在此手動發放（測試、贈送，到期日必填，不改變自動續期）。
          續期扣款失敗會進入 3 天「寬限中」，期間仍可觀看。全站 VIP 不會解鎖鐵粉內容。
        </p>
        <div className="grid gap-3 md:grid-cols-[1fr_180px_1fr_auto] md:items-end">
          <div>
            <Label htmlFor="fans-email">粉絲 Email（App 登入帳號）</Label>
            <Input id="fans-email" type="email" value={email} disabled={busy} placeholder="fan@example.com" onChange={(e) => setEmail(e.target.value)} />
          </div>
          <div>
            <Label htmlFor="fans-until">到期日（必填）</Label>
            <Input id="fans-until" type="date" value={until} disabled={busy} onChange={(e) => setUntil(e.target.value)} />
          </div>
          <div>
            <Label htmlFor="fans-note">備註（選填，≤200 字）</Label>
            <Input id="fans-note" value={note} maxLength={200} disabled={busy} placeholder="例：10/31 上線測試" onChange={(e) => setNote(e.target.value)} />
          </div>
          <Button disabled={busy || !email.trim() || !until} onClick={() => grant.mutate()}>
            發放鐵粉權益
          </Button>
        </div>
        {error && (
          <p role="alert" className="text-destructive">
            {error instanceof Error ? error.message : "操作未完成，請重試"}
          </p>
        )}
        {message && <p role="status">{message}</p>}
        <div className="flex flex-wrap items-center gap-2" role="group" aria-label="依狀態篩選">
          <span className="text-sm text-muted-foreground">狀態：</span>
          {statusFilters.map((f) => (
            <Button
              key={f.value || "all"}
              size="sm"
              variant={statusFilter === f.value ? "default" : "outline"}
              aria-pressed={statusFilter === f.value}
              onClick={() => setStatusFilter(f.value)}
            >
              {f.label}
            </Button>
          ))}
        </div>
        {list.isPending && <p>讀取名單…</p>}
        {list.data && list.data.items.length === 0 && (
          <p className="text-sm text-muted-foreground">{statusFilter ? "沒有符合此狀態的鐵粉。" : "尚無任何鐵粉權益。"}</p>
        )}
        {list.data && list.data.items.length > 0 && (
          <table className="w-full text-sm">
            <thead>
              <tr className="text-left text-muted-foreground">
                <th className="py-1 pr-3">帳號</th>
                <th className="py-1 pr-3">狀態</th>
                <th className="py-1 pr-3">來源</th>
                <th className="py-1 pr-3">自動續期</th>
                <th className="py-1 pr-3">連續月數</th>
                <th className="py-1 pr-3">效期</th>
                <th className="py-1 pr-3">備註</th>
                <th className="py-1" />
              </tr>
            </thead>
            <tbody>
              {list.data.items.map((e) => (
                <tr key={e.uid} className="border-t align-top">
                  <td className="py-2 pr-3 break-all">
                    {e.email ?? "—"}
                    <span className="block text-xs text-muted-foreground">{e.uid}</span>
                  </td>
                  <td className="py-2 pr-3">
                    <AdminStatusBadge label={statusLabels[e.status] ?? e.status} tone={statusTone[e.status] ?? "neutral"} />
                    {e.status === "grace" && <span className="mt-1 block text-xs text-amber-800 whitespace-nowrap">寬限至 {fmt(e.graceUntil ?? null)}</span>}
                  </td>
                  <td className="py-2 pr-3">{sourceLabels[e.source ?? ""] ?? e.source ?? "—"}</td>
                  <td className="py-2 pr-3">{e.autoRenew == null ? "—" : e.autoRenew ? "開" : "關"}</td>
                  <td className="py-2 pr-3">{e.streakMonths ? `${e.streakMonths} 個月` : "—"}</td>
                  <td className="py-2 pr-3 whitespace-nowrap">
                    {fmt(e.validFrom)}
                    <span className="block">→ {fmt(e.validUntil)}</span>
                  </td>
                  <td className="py-2 pr-3 break-words">{e.note || "—"}</td>
                  <td className="py-2 text-right">
                    {e.status !== "revoked" && (
                      <Button variant="outline" size="sm" disabled={busy} onClick={() => revoke.mutate(e.uid)}>
                        撤銷
                      </Button>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
        {list.data?.nextCursor && (
          <p className="text-sm text-muted-foreground">名單超過 50 筆，只顯示最近更新的 50 筆（可用狀態篩選縮小範圍）。</p>
        )}
      </CardContent>
    </Card>
  );
}
